/**
 * ComputedAttributesTableRow - Linha da tabela de atributos calculados
 * Memoizado para evitar re-renders desnecessários
 */

import React, { memo } from 'react';
import {
  TableRow,
  TableCell,
  Typography,
  Avatar,
  Chip,
  Checkbox,
  Tooltip,
  IconButton,
  Stack,
} from '@mui/material';
import FunctionsIcon from '@mui/icons-material/Functions';
import CodeIcon from '@mui/icons-material/Code';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useTranslation } from '../../common/components/LocalizationProvider';
import usePositionAttributes from '../../common/attributes/usePositionAttributes';
import CollectionActions from './CollectionActions';

// Helper para cores do status - USA TOKENS DINÂMICOS
const getStatusColors = (status, tokens) => {
  const colors = {
    active: {
      text: tokens?.semantic?.success,
      bg: tokens?.semantic?.successBg,
      border: `${tokens?.semantic?.success}40`,
    },
    disabled: {
      text: tokens?.semantic?.error,
      bg: tokens?.semantic?.errorBg,
      border: `${tokens?.semantic?.error}40`,
    },
  };

  return colors[status] || colors.active;
};

// Helper para cores do tipo
const getTypeColors = (type, tokens) => {
  if (type === 'string') {
    return {
      bg: tokens?.semantic?.infoBg,
      color: tokens?.semantic?.info,
      border: `${tokens?.semantic?.info}40`,
    };
  }
  return {
    bg: tokens?.semantic?.warningBg,
    color: tokens?.semantic?.warning,
    border: `${tokens?.semantic?.warning}40`,
  };
};

const ComputedAttributesTableRow = memo(({
  row,
  onRefresh,
  onTest,
  selected = false,
  onSelect,
  isAdmin = false,
}) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const positionAttributes = usePositionAttributes(t);

  const status = row.disabled ? 'disabled' : 'active';
  const statusColors = getStatusColors(status, tokens);
  const typeColors = getTypeColors(row.type, tokens);

  // Cor do avatar baseada no status
  const avatarColor = statusColors.text;

  // Formatação do nome do atributo
  const formatAttributeName = (attributeKey) => {
    if (!attributeKey) return '';
    if (positionAttributes && positionAttributes[attributeKey]?.name) {
      return positionAttributes[attributeKey].name;
    }
    return attributeKey.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
  };

  return (
    <TableRow
      hover
      selected={selected}
      sx={{
        cursor: 'default',
        transition: tokens?.transitions?.colors || 'all 0.15s ease',
        '&:hover': {
          backgroundColor: tokens?.surface?.hover || 'rgba(255,255,255,0.03)',
        },
        '&.Mui-selected': {
          backgroundColor: `${tokens?.accent?.primary}10`,
          '&:hover': {
            backgroundColor: `${tokens?.accent?.primary}15`,
          },
        },
      }}
    >
      {/* Checkbox */}
      {onSelect && (
        <TableCell padding="checkbox" sx={{ py: 1, width: 48 }}>
          <Checkbox
            checked={selected}
            onChange={() => onSelect(row.id)}
            sx={{
              color: tokens?.text?.tertiary,
              '&.Mui-checked': {
                color: tokens?.accent?.primary,
              },
            }}
          />
        </TableCell>
      )}

      {/* Avatar */}
      <TableCell sx={{ py: 1, width: 48 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: `${avatarColor}20`,
            color: avatarColor,
          }}
        >
          <FunctionsIcon fontSize="small" />
        </Avatar>
      </TableCell>

      {/* ID */}
      <TableCell sx={{ py: 1, width: 60 }}>
        <Typography variant="body2" sx={{ color: tokens?.text?.muted, fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {row.id}
        </Typography>
      </TableCell>

      {/* Description */}
      <TableCell sx={{ py: 1 }}>
        <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 180 }}>
          {row.description || '-'}
        </Typography>
      </TableCell>

      {/* Attribute */}
      <TableCell sx={{ py: 1 }}>
        <Chip
          label={formatAttributeName(row.attribute)}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.7rem',
            backgroundColor: tokens?.surface?.elevated,
            color: tokens?.text?.primary,
            border: `1px solid ${tokens?.border?.subtle}`,
          }}
        />
      </TableCell>

      {/* Expression */}
      <TableCell sx={{ py: 1, maxWidth: 200 }}>
        <Tooltip title={row.expression || '-'}>
          <Chip
            icon={<CodeIcon sx={{ fontSize: 14 }} />}
            label={row.expression?.slice(0, 25) + (row.expression?.length > 25 ? '...' : '') || '-'}
            size="small"
            variant="outlined"
            sx={{
              height: 22,
              fontSize: '0.65rem',
              borderColor: tokens?.border?.subtle,
              color: tokens?.text?.secondary,
              '& .MuiChip-icon': { color: tokens?.text?.muted },
            }}
          />
        </Tooltip>
      </TableCell>

      {/* Type */}
      <TableCell sx={{ py: 1 }}>
        <Chip
          label={row.type || '-'}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 600,
            backgroundColor: typeColors.bg,
            color: typeColors.color,
            border: `1px solid ${typeColors.border}`,
          }}
        />
      </TableCell>

      {/* Status */}
      <TableCell align="center" sx={{ py: 1 }}>
        <Chip
          label={row.disabled ? t('sharedDisabled') : t('deviceStatusActive')}
          size="small"
          className={row.disabled ? 'status-disabled' : 'status-active'}
          sx={{
            minWidth: 70,
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 600,
            backgroundColor: statusColors.bg,
            color: statusColors.text,
            border: `1px solid ${statusColors.border}`,
            '& .MuiChip-label': { px: 1 },
          }}
        />
      </TableCell>

      {/* Actions */}
      {isAdmin && (
        <TableCell align="right" sx={{ py: 1 }}>
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            {onTest && (
              <Tooltip title={t('attributeTest')}>
                <IconButton
                  size="small"
                  onClick={() => onTest(row.id)}
                  sx={{
                    color: tokens?.semantic?.success,
                    '&:hover': {
                      backgroundColor: `${tokens?.semantic?.success}15`,
                    },
                  }}
                >
                  <PlayArrowIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <CollectionActions
              itemId={row.id}
              editPath="/settings/attribute"
              endpoint="attributes/computed"
              setTimestamp={onRefresh}
            />
          </Stack>
        </TableCell>
      )}
    </TableRow>
  );
});

ComputedAttributesTableRow.displayName = 'ComputedAttributesTableRow';

export default ComputedAttributesTableRow;
